import {
  isNil,
  contains,
  prop,
  path,
  propEq,
  isEmpty,
  filter,
  anyPass,
  includes,
  compose,
  join,
  without,
  values,
  equals,
  reject,
} from 'ramda';
import { MISSION_STATUS, MISSION_TYPE, VEHICLE_DESINATION_TYPE, ERROR_CODES, HISTORY_EVENTS } from '../enums';
import {
  APPROVED,
  IDENTIFIED,
  AUTHENTICATED,
  getMission,
  getMissionStatus as getCurrentMissionStatus,
  getScopeStatus,
  getAccessToken,
  getCarDestinations,
  getShowCustomDestination,
  getClaim,
  getCountry,
  getClaimHighwayMetres,
  getHighway,
  getHasHighwayDirection,
  getCarDestination,
  getAppointment,
  getClaimId,
  getHasRecommendedNetworks,
  getMissionType,
  getCarDestinationType,
  getMissions,
  getIsClaimNotDisplayed,
  getAllowHidingDestinationProcessInDigital,
  getEnergy,
  getSpecificDestinationsLoaded,
  getCoreRentalMissionStatus,
  getGarageImmobilizationName,
  getGarageImmobilizationFullAddress,
  getCarDestinationProviderName,
  getCarDestinationFullAddress,
  getCurrentLimitsRemainingRentalDays,
  getDigitalRentalWoFirstService,
} from '../selectors/coreApi';
import { getErrorCode } from '../selectors/error';
import { getStatus } from '../selectors/missionMonitoring';
import { getAppointmentDeactivation, getIsPublicApiV1, getIsPublicApiV4 } from '../selectors/settings';
import { getWorkflowState } from '../selectors/machine';
import states from '../fsm/states';
import { isRequestError403, isRequestError404 } from './error';

export const alreadyHasToken = getAccessToken;
const isScope = scope => state => getScopeStatus(state) === scope;
export const isScopeAuthenticated = isScope(AUTHENTICATED);
export const isScopeIdentified = isScope(IDENTIFIED);
export const isScopeApproved = isScope(APPROVED);

const ROOT = 'coreApi';
const TOW = 'TOW';
const ROS = 'ROS';
const TAXI = 'TAXI';
const RENTAL_CAR = 'RENTAL_CAR';
const VEHICLE_TRANSPORTATION = 'VEHICLE_TRANSPORTATION';
const HIGHWAY_TOW = 'HIGHWAY_TOW';
const HIGHWAY_ROS = 'HIGHWAY_ROS';
const FR = 'FR';
const ES = 'ES';
const IT = 'IT';
const PT = 'PT';

export const isROSService = state => isROSMission(getMission(state));
export const isTOWService = state => isTOWMission(getMission(state));
export const isTOWMission = anyPass([propEq('service', TOW), propEq('service', HIGHWAY_TOW)]);
export const isROSMission = anyPass([propEq('service', ROS), propEq('service', HIGHWAY_ROS)]);
export const isCancelledMission = propEq('status', MISSION_STATUS.CANCELLED);
export const isRejectedMission = propEq('status', MISSION_STATUS.REJECTED);
export const isTimeoutMission = propEq('status', MISSION_STATUS.TIMEOUT);
export const hasMission = state => !isEmpty(getMission(state));
export const isTaxiService = propEq('service', TAXI);
export const isRentalService = propEq('service', RENTAL_CAR);
export const isVLTService = propEq('service', VEHICLE_TRANSPORTATION);
export const isTOWOrROS = anyPass([isTOWMission, isROSMission]);
export const isFirstOrSecondService = anyPass([isTOWOrROS, isTaxiService, isRentalService, isVLTService]);
const isCancelledRejectedTimeout = anyPass([isRejectedMission, isTimeoutMission]);

export const getFiltredServices = compose(
  reject(isCancelledRejectedTimeout),
  filter(isFirstOrSecondService),
  getMissions,
);

export const hasPossibleDestination = state =>
  (getHasRecommendedNetworks(state) && !isEmpty(getCarDestinations(state))) || getShowCustomDestination(state);

const MISSION_MONITORING_STATUSES = [
  MISSION_STATUS.ACCEPTED,
  MISSION_STATUS.INTERMEDIARY_STOP,
  MISSION_STATUS.PENDING,
  MISSION_STATUS.READY,
  MISSION_STATUS.START_TOW,
  MISSION_STATUS.STARTED,
  MISSION_STATUS.DISPATCHED,
];

const MISSION_PROVIDER_ARRIVING_STATUSES = [
  MISSION_STATUS.ACCEPTED,
  MISSION_STATUS.DISPATCHED,
  MISSION_STATUS.STARTED,
  MISSION_STATUS.START_TOW,
  MISSION_STATUS.INTERMEDIARY_STOP,
  MISSION_STATUS.ON_SPOT,
];

const MISSION_END_STATUSES = [MISSION_STATUS.ETA_EXPIRED, MISSION_STATUS.END, MISSION_STATUS.ON_SPOT];

const MISSION_CANCELLED_STATUSES = [
  MISSION_STATUS.OPERATOR_CANCELLED,
  MISSION_STATUS.OPERATOR_FORCE_CANCELLED,
  MISSION_STATUS.CANCELLED,
];

const MISSION_REJECT_TIMEOUT_STATUSES = [MISSION_STATUS.REJECTED, MISSION_STATUS.TIMEOUT];

const RENTAL_MISSION_ONGOING_STATUSES = [
  MISSION_STATUS.IN_CREATION,
  MISSION_STATUS.PENDING,
  MISSION_STATUS.ACCEPTED,
  MISSION_STATUS.STARTED,
  MISSION_STATUS.END,
];

const RENTAL_MISSION_BLOCKING_STATUSES = [
  MISSION_STATUS.IN_CREATION,
  MISSION_STATUS.READY,
  MISSION_STATUS.PENDING,
  MISSION_STATUS.ACCEPTED,
  MISSION_STATUS.STARTED,
  MISSION_STATUS.DISPATCHED,
  MISSION_STATUS.END,
];

const RENTAL_MISSION_SUMMARY_ALLOWANCE_STATUSES = [
  MISSION_STATUS.PENDING,
  MISSION_STATUS.ACCEPTED,
  MISSION_STATUS.STARTED,
];

export const RENTAL_MISSION_END_STATUSES = [
  MISSION_STATUS.END,
  MISSION_STATUS.MISSION_REFUSED,
  MISSION_STATUS.REJECTED,
  MISSION_STATUS.TIMEOUT,
];

const CANCEL_MISSION_COST_METHODS = ['cancelMissionCostV4', 'cancelMissionCostV5'];

export const isTowOnSpot = state => getStatus(state) === MISSION_STATUS.ON_SPOT;
export const isMissionTypeAppointment = state => getMissionType(state) === MISSION_TYPE.APPOINTMENT;

const isEventFilled = ({ event }) => !isNil(event);
const isLocationFilled = ({ location }) => !isNil(location);
const isCoveredGarageFilled = ({ locationDetails: { coveredGarage } = {} }) => !isNil(coveredGarage);
const isHighwayFilled = ({ locationDetails: { highway } = {} }) => !isNil(highway);

export const isEligible = state => !isNil(getClaimId(state));
export const isCancelMissionCostFailed = state => {
  return CANCEL_MISSION_COST_METHODS.includes(state?.error?.method);
};
export const isClaimCompleted = state => {
  const claim = getClaim(state);
  if (getIsPublicApiV1(state)) return isEventFilled(claim) && isLocationFilled(claim);
  if (isDigitalHighwayProcess(state)) return getClaimHighwayMetres(state) && getHasHighwayDirection(state);
  return isEventFilled(claim) && isLocationFilled(claim) && isCoveredGarageFilled(claim) && isHighwayFilled(claim);
};

export const shouldFillEventClaim = claim => !isEventFilled(claim);
export const shouldFillLocationClaim = claim => !shouldFillEventClaim(claim) && !isLocationFilled(claim);
export const shouldFillLocationDetailsClaim = claim =>
  !shouldFillEventClaim(claim) &&
  !shouldFillLocationClaim(claim) &&
  !isCoveredGarageFilled(claim) &&
  !isHighwayFilled(claim);
export const shouldFillHighwayMetresClaim = state => {
  const claim = getClaim(state);
  return !shouldFillLocationDetailsClaim(claim) && isDigitalHighwayProcess(state) && !getClaimHighwayMetres(state);
};
export const shouldFillHighwayDirectionClaim = state => {
  return !shouldFillHighwayMetresClaim(state) && isDigitalHighwayProcess(state) && !getHasHighwayDirection(state);
};
export const shouldFillCarDestination = state =>
  !getAllowHidingDestinationProcessInDigital(state) &&
  isClaimCompleted(state) &&
  (isTOWService(state) || isROSService(state)) &&
  !getCarDestination(state) &&
  hasPossibleDestination(state);

export const shouldFillAppointment = state => {
  if (getIsPublicApiV1(state))
    return (
      !shouldFillCarDestination(state) &&
      !isMissionTypeAppointment(state) &&
      !isAppointmentDeactivated(state) &&
      !isDigitalHighwayProcess(state)
    );

  return (
    !shouldFillCarDestination(state) &&
    !getAppointment(state) &&
    !isAppointmentDeactivated(state) &&
    !isDigitalHighwayProcess(state)
  );
};
export const isMissionInCreation = state => getCurrentMissionStatus(state) === MISSION_STATUS.IN_CREATION;
export const isMissionInProgress = state => contains(getCurrentMissionStatus(state), MISSION_MONITORING_STATUSES);
export const isMissionProviderArriving = state =>
  contains(getCurrentMissionStatus(state), MISSION_PROVIDER_ARRIVING_STATUSES);
export const isMissionEnded = state => contains(getCurrentMissionStatus(state), MISSION_END_STATUSES);
export const isMissionNotYetDispatched = state => !isMissionInProgress(state) && !isMissionEnded(state);
export const isMissionMonitoringIsEnded = state => contains(getCurrentMissionStatus(state), MISSION_END_STATUSES);
export const isNpsConsentEligibilityNeeded = state => getNpsEligibilityClaim(state);
export const isCountryFR = state => getCountry(state) === FR;
export const isCountryES = state => getCountry(state) === ES;
export const isCountryIT = state => getCountry(state) === IT;
export const isCountryPT = state => getCountry(state) === PT;

export const isRecommendedVehicleDestination = state =>
  equals(VEHICLE_DESINATION_TYPE.RECOMMENDED, getCarDestinationType(state));

export const isDigitalHighwayProcess = state =>
  getHighway(state) && (isCountryES(state) || isCountryIT(state) || isCountryPT(state));

export const isSpecificDestinationsLoaded = state => !!getSpecificDestinationsLoaded(state);

export const isMissionRejectedOrTimeOut = state =>
  contains(getCurrentMissionStatus(state), MISSION_REJECT_TIMEOUT_STATUSES);
export const isMissionCancelled = state => contains(getCurrentMissionStatus(state), MISSION_CANCELLED_STATUSES);

export const isRentalMissionCancelled = state =>
  contains(getCoreRentalMissionStatus(state), MISSION_CANCELLED_STATUSES);
export const isRentalMissionRejectedOrTimeOut = state =>
  contains(getCoreRentalMissionStatus(state), MISSION_REJECT_TIMEOUT_STATUSES);
export const isRentalMissionSummaryAllowed = state =>
  contains(getCoreRentalMissionStatus(state), RENTAL_MISSION_SUMMARY_ALLOWANCE_STATUSES);
export const isRentalMissionEnded = state => getCoreRentalMissionStatus(state) === MISSION_STATUS.END;
export const isRentalMissionInCreation = state => getCoreRentalMissionStatus(state) === MISSION_STATUS.IN_CREATION;
export const isRentalMissionOngoing = mission => contains(mission?.status, RENTAL_MISSION_ONGOING_STATUSES);
export const isCoreRentalMissionOngoing = state =>
  contains(getCoreRentalMissionStatus(state), RENTAL_MISSION_BLOCKING_STATUSES);
export const isDigitalRentalWoFirstService = state => getDigitalRentalWoFirstService(state);

/**
 * Retrieves the latest mission from a list of missions that satisfies a given predicate.
 *
 * @param {Array<Object>} missions - The array of mission objects to search through.
 * @param {Function} predicate - A function that takes a mission object as an argument and returns a boolean indicating whether the mission satisfies the condition.
 * @returns {Object|string} The latest mission object that satisfies the predicate, or an empty string if no such mission exists.
 */
export const getLatestMissionById = (missions, predicate) => {
  if (!missions || !missions.length) return '';

  return (
    missions.reduce((latest, mission) => {
      if (predicate(mission) && (!latest || mission.id > latest.id)) {
        return mission;
      }
      return latest;
    }, null) || ''
  );
};

export const lastMissionTowOrRos = missions => getLatestMissionById(missions, anyPass([isTOWMission, isROSMission]));

export const lastMissionTow = missions => getLatestMissionById(missions, isTOWMission);

export const lastTOWOrVLTMission = missions => getLatestMissionById(missions, anyPass([isVLTService, isTOWMission]));

export const isAppointmentDeactivated = getAppointmentDeactivation;

export const shouldPutVehicleDestination = state => {
  return (
    (getIsPublicApiV1(state) &&
      isTOWService(state) &&
      (!hasPossibleDestination(state) || getAllowHidingDestinationProcessInDigital(state))) ||
    (isROSService(state) && getAllowHidingDestinationProcessInDigital(state))
  );
};
const { EVENTS_DETAILS, VEHICLE_LOCATION, LOCATION_DETAILS, MILESTONE, HIGHWAY_DIRECTION } = states;

export const isWorkflowBackButtonEnabled = state =>
  includes(getWorkflowState(state), [EVENTS_DETAILS, VEHICLE_LOCATION, LOCATION_DETAILS, MILESTONE, HIGHWAY_DIRECTION]);

export const getRentalPickupTime = prop('rentalPickupTime');

export const getRentalDuration = prop('rentalDuration');

export const getRentalPickupProviderName = path(['pickupProvider', 'name']);

export const getRentalPickupPhone = path(['pickupProvider', 'contact', 'phone1']);

export const getRentalDropoffProviderName = path(['dropoffProvider', 'name']);

export const getRentalDropoffPhone = path(['dropoffProvider', 'contact', 'phone1']);

export const getRentalPickupAddress = compose(
  join(' '),
  without([undefined]),
  values,
  path(['pickupLocation', 'address']),
);

export const getRentalDropoffAddress = compose(
  join(' '),
  without([undefined]),
  values,
  path(['dropoffLocation', 'address']),
);

export const isVehicleImmobilizationOrVehicleDestinationAdded = state =>
  (getGarageImmobilizationName(state) && getGarageImmobilizationFullAddress(state)) ||
  (getCarDestinationProviderName(state) && getCarDestinationFullAddress(state));

export const getTaxiReservationNumber = prop('reservationNumber');

export const getTaxiPickUpTime = prop('taxiPickupTime');

export const getTaxiProviderName = path(['provider', 'name']);

export const getTaxiPassengersCount = prop('passengerCount');

export const getTaxiLuggageCount = prop('luggageCount');

export const getTaxiPickupAddress = compose(
  join(' '),
  without([undefined]),
  values,
  path(['pickUpLocation', 'address']),
);

export const getTaxiDropoffAddress = compose(
  join(' '),
  without([undefined]),
  values,
  path(['dropOffLocation', 'address']),
);

export const getMissionCarDestination = compose(
  join(' '),
  without([undefined]),
  values,
  path(['vehicleDestination', 'provider', 'location', 'address']),
);

export const getMissionCarDestinationFromHistoryItem = compose(
  join(' '),
  without([undefined]),
  values,
  path(['vehicleDestination', 'provider', 'location', 'address']),
);

export const getMissionProviderName = path(['provider', 'name']);
export const getMissionStatus = prop('status');
export const getMissionId = prop('id');

export const getNpsEligibilityClaim = path([ROOT, 'npsConsent']);

export const isNpsNeeded = path([ROOT, 'npsConsent', 'npsConsentNeeded']);

export const isNpsAvailable = path([ROOT, 'npsConsent', 'npsConsentAvailable']);

export const isClaimNotDisplayed = state =>
  getIsPublicApiV4(state) &&
  (getIsClaimNotDisplayed(state) ||
    (isRequestError403(state) && equals(getErrorCode(state), ERROR_CODES.RESOURCE_ACCESS_ISSUE)) ||
    (isRequestError404(state) && equals(getErrorCode(state), ERROR_CODES.CLAIM_NOT_FOUND)));

export const isHybridVehicle = state =>
  equals(getEnergy(state), 'PLUGIN_HYBRID') || equals(getEnergy(state), 'FULL_HYBRID');

export const extractUniqueClaimHistory = claimHistory => {
  return claimHistory.reduce((acc, currentValue) => {
    if (acc.find(event => event.type === currentValue.type)) {
      return acc;
    }
    return [...acc, currentValue];
  }, []);
};

const EVENTS_WITHOUT_FILTERING_LAST_ONE = [
  HISTORY_EVENTS.MISSION_COPIED,
  HISTORY_EVENTS.RENTAL_BOOKING_DETAILS_UPDATED,
];

export const extractMissionHistory = (missionHistory, state) => {
  if (!state.missions) {
    return [];
  }

  const mostRecentTowOrVltMission = lastTOWOrVLTMission(state.missions);

  return missionHistory.reduce((acc, currentValue) => {
    if (
      currentValue.type !== HISTORY_EVENTS.MISSION_DRAFT_CREATED &&
      currentValue.missionId &&
      currentValue.missionId !== mostRecentTowOrVltMission.id &&
      currentValue.type !== HISTORY_EVENTS.INTERMEDIATE_STOP_SELECTED &&
      currentValue.type !== HISTORY_EVENTS.INTERMEDIATE_STOP_CLEARED &&
      currentValue.type !== HISTORY_EVENTS.MISSION_COPIED &&
      !(currentValue.type === HISTORY_EVENTS.RENTAL_BOOKING_DETAILS_UPDATED && currentValue?.durationInMinutes) &&
      (currentValue.type !== HISTORY_EVENTS.VEHICLE_DESTINATION_SELECTED ||
        currentValue.missionId !== mostRecentTowOrVltMission?.id)
    ) {
      return acc;
    }

    const existingIndex = acc.findIndex(
      event => !contains(currentValue.type, EVENTS_WITHOUT_FILTERING_LAST_ONE) && event.type === currentValue.type,
    );

    if (
      currentValue.type !== HISTORY_EVENTS.MISSION_DRAFT_CREATED &&
      currentValue.type !== HISTORY_EVENTS.VEHICLE_DESTINATION_SELECTED &&
      existingIndex !== -1
    ) {
      return [...acc.slice(0, existingIndex), currentValue, ...acc.slice(existingIndex + 1)];
    }

    return [...acc, currentValue];
  }, []);
};

export const isCoveredForRentalExtension = state => getCurrentLimitsRemainingRentalDays(state) > 0;
